import React, { useState, useEffect } from 'react';
import { companiesAPI, notesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  Scale,
  X
} from 'lucide-react';

const NotesPage = () => {
  const [notes, setNotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ company: 'all', type: 'all' });
  
  // New note form
  const [showForm, setShowForm] = useState(false);
  const [newNote, setNewNote] = useState({
    company_id: '',
    kr_id: '',
    note_type: 'evidence',
    content: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [notesRes, companiesRes] = await Promise.all([
        notesAPI.getAll({
          company_id: filter.company !== 'all' ? filter.company : undefined,
          note_type: filter.type !== 'all' ? filter.type : undefined
        }),
        companiesAPI.getAll()
      ]);
      
      setNotes(notesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Error al cargar notas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleCreateNote = async () => {
    if (!newNote.content || !newNote.company_id) {
      toast.error('Completa todos los campos');
      return;
    }
    
    try {
      await notesAPI.create({
        ...newNote,
        kr_id: newNote.kr_id || 'general'
      });
      toast.success('Nota creada');
      setShowForm(false);
      setNewNote({ company_id: '', kr_id: '', note_type: 'evidence', content: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear nota');
    }
  };

  const getNoteTypeIcon = (type) => {
    const icons = {
      blocker: <AlertTriangle className="w-4 h-4" />,
      evidence: <CheckCircle2 className="w-4 h-4" />,
      proposal: <Lightbulb className="w-4 h-4" />,
      decision: <Scale className="w-4 h-4" />
    };
    return icons[type] || <FileText className="w-4 h-4" />;
  };

  const getNoteTypeColor = (type) => {
    const colors = {
      blocker: 'text-red-700 bg-red-50 border-red-200',
      evidence: 'text-emerald-700 bg-emerald-50 border-emerald-200',
      proposal: 'text-blue-700 bg-blue-50 border-blue-200',
      decision: 'text-amber-700 bg-amber-50 border-amber-200'
    };
    return colors[type] || 'text-gray-700 bg-gray-50 border-gray-200';
  };

  const getNoteTypeLabel = (type) => {
    const labels = {
      blocker: 'Bloqueo',
      evidence: 'Evidencia',
      proposal: 'Propuesta',
      decision: 'Decisión'
    };
    return labels[type] || type;
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company?.code || 'N/A';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-gray-700" />
            Notes & Evidence
          </h1>
          <p className="text-gray-500 mt-1">
            Registro de bloqueos, evidencias, propuestas y decisiones
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-gray-900 hover:bg-gray-800 text-white"
            data-testid="new-note-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Nota
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            className="border-gray-200 hover:bg-gray-100"
            onClick={fetchData}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select 
          value={filter.company} 
          onValueChange={(v) => setFilter(prev => ({ ...prev, company: v }))}
        >
          <SelectTrigger className="w-48 bg-white border-gray-200">
            <SelectValue placeholder="Empresa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las empresas</SelectItem>
            {companies.map((company) => (
              <SelectItem key={company.company_id} value={company.company_id}>
                {company.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select 
          value={filter.type} 
          onValueChange={(v) => setFilter(prev => ({ ...prev, type: v }))}
        >
          <SelectTrigger className="w-48 bg-white border-gray-200">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="blocker">Bloqueos</SelectItem>
            <SelectItem value="evidence">Evidencias</SelectItem>
            <SelectItem value="proposal">Propuestas</SelectItem>
            <SelectItem value="decision">Decisiones</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* New Note Form */}
      {showForm && (
        <div className="bg-white rounded-lg border border-blue-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Nueva Nota</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowForm(false)}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Select 
                value={newNote.company_id} 
                onValueChange={(v) => setNewNote(prev => ({ ...prev, company_id: v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.company_id} value={company.company_id}>
                      {company.code} - {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={newNote.note_type} 
                onValueChange={(v) => setNewNote(prev => ({ ...prev, note_type: v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Tipo de nota" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocker">Bloqueo</SelectItem>
                  <SelectItem value="evidence">Evidencia</SelectItem>
                  <SelectItem value="proposal">Propuesta</SelectItem>
                  <SelectItem value="decision">Decisión</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Textarea
              placeholder="Contenido de la nota..."
              value={newNote.content}
              onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white min-h-[100px]"
              data-testid="note-content-input"
            />

            <Button 
              onClick={handleCreateNote} 
              className="bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="create-note-btn"
            >
              Crear Nota
            </Button>
          </div>
        </div>
      )}

      {/* Notes List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : notes.length > 0 ? (
        <div className="grid gap-4">
          {notes.map((note) => (
            <div 
              key={note.note_id}
              className={`bg-white rounded-lg border p-4 ${getNoteTypeColor(note.note_type)}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`p-1.5 rounded ${getNoteTypeColor(note.note_type)}`}>
                    {getNoteTypeIcon(note.note_type)}
                  </span>
                  <span className="text-sm font-medium uppercase">
                    {getNoteTypeLabel(note.note_type)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    {getCompanyName(note.company_id)}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(note.created_at).toLocaleDateString('es-ES')}
                  </span>
                </div>
              </div>
              
              <p className="text-gray-700">{note.content}</p>
              
              {note.author_name && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">
                    Por {note.author_name}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay notas</h3>
          <p className="text-gray-500">
            Crea tu primera nota para comenzar a documentar
          </p>
        </div>
      )}
    </div>
  );
};

export default NotesPage;
